import { useState } from "react";
import { useStripe } from "@stripe/react-stripe-js";

import { captureException } from "common/utils/monitoring";
import { OrderPriceSummary } from "common/types/priceSummary";
import { PaymentMethodOption } from "common/types";
import { deepCombineErrors } from "common/utils/errors";

import DrawerLayout from "client/common/components/stackables/DrawerLayout";
import { useClientQuery } from "client/accounts/hooks";
import {
  getWalletPaymentItems,
  isPaymentOptionChargeable,
  UNCHARGEABLE_PAYMENT_METHOD_MESSAGE,
  walletDisplayText,
} from "client/payments/utils";
import PaymentMethodSelectPanel from "client/payments/components/PaymentMethodSelectPanel";
import CardPaymentDeclineAlert from "client/common/components/content/CardPaymentDeclineAlert";
import { usePaymentCreateDrawer } from "client/payments/stackables";
import { useCurrentDrawer } from "client/common/hooks/stackables";
import {
  useCreatePaymentMethodMutation,
  useWalletPaymentMethodRequest,
} from "client/payments/hooks";

import { useOrderQuery, useUpdateOrderPaymentMethodMutation } from "../hooks";
import { IOrder } from "../types";

export type OrderPaymentUpdateDrawerProps = {
  id: number;
};

type OrderPaymentUpdateFormProps = {
  order: IOrder;
};

const OrderPaymentUpdateForm = ({ order }: OrderPaymentUpdateFormProps) => {
  const { close } = useCurrentDrawer();
  const { open: openPaymentCreateDrawer } = usePaymentCreateDrawer();
  const { data: client, isLoading: clientLoading, error: clientError } = useClientQuery();

  const methods = client ? client.payment_methods : [];
  const [selectedPaymentOption, setSelectedPaymentOption] = useState<
    PaymentMethodOption | undefined
  >(order.payment_method);

  const stripe = useStripe();
  const {
    mutate: updateOrderPayment,
    isPending: isUpdateLoading,
    error: updateError,
    reset: resetUpdateOrderPayment,
  } = useUpdateOrderPaymentMethodMutation(order.id, {
    onSuccess: close,
    onError: (error, data) => {
      if (!stripe || !error.data?.stripe_action_client_secret) {
        return;
      }
      stripe.handleCardAction(error.data?.stripe_action_client_secret).then((result) => {
        if (result.paymentIntent) {
          updateOrderPayment(data);
        }
      });
    },
  });

  const {
    mutateAsync: createWalletPaymentMethod,
    isPending: isCreateWalletPaymentMethodLoading,
    error: createWalletPaymentMethodError,
  } = useCreatePaymentMethodMutation();

  const {
    supportedWallets,
    isLoading: isLoadingPaymentRequest,
    request: walletPaymentRequest,
  } = useWalletPaymentMethodRequest({
    label: `Order ${order.display_number}`,
    amount: order.total,
    paymentItems: getWalletPaymentItems<OrderPriceSummary>(order),
    onCreate: async (id: string) => {
      resetUpdateOrderPayment(); // reset the update order payment error
      const method = await createWalletPaymentMethod({
        payment_method: id,
      });
      updateOrderPayment({ payment_method_id: method.id });
    },
  });

  const selectedPaymentIsUnchargeable =
    !!selectedPaymentOption && !isPaymentOptionChargeable(selectedPaymentOption);

  const canSubmit = !!(
    selectedPaymentOption &&
    !selectedPaymentIsUnchargeable &&
    !isCreateWalletPaymentMethodLoading &&
    !isUpdateLoading
  );

  const handleSubmit = () => {
    if (!selectedPaymentOption) {
      return;
    }

    if (selectedPaymentOption.id === "__wallet__") {
      walletPaymentRequest?.show();
      return;
    }

    if (typeof selectedPaymentOption.id === "number") {
      updateOrderPayment({ payment_method_id: selectedPaymentOption.id });
      return;
    }

    captureException(
      new Error(
        `Unexpected placeholder payment method [${JSON.stringify(selectedPaymentOption)}] in OrderPaymentUpdateDrawer`,
      ),
    );
  };

  if (clientLoading) {
    return <DrawerLayout.Loading />;
  }

  if (clientError) {
    return <DrawerLayout.FetchError entityName="Payment method" error={clientError} />;
  }

  const declineError = updateError?.data?.card_decline_info;

  const formError = createWalletPaymentMethodError || updateError;

  return (
    <DrawerLayout>
      <DrawerLayout.Header title="Update payment method" onClose={close} />
      <DrawerLayout.Form
        submitText={
          selectedPaymentOption?.id === "__wallet__"
            ? `Authorize ${walletDisplayText(selectedPaymentOption.wallet)} payment`
            : "Authorize Payment"
        }
        canSubmit={canSubmit}
        submitTooltipContent={
          selectedPaymentIsUnchargeable ? UNCHARGEABLE_PAYMENT_METHOD_MESSAGE : undefined
        }
        isLoading={isCreateWalletPaymentMethodLoading || isUpdateLoading}
        // Don't need to show generic error if there is a decline error
        error={declineError ? null : formError}
        fieldErrors={[deepCombineErrors(formError?.errors)]}
        onSubmit={handleSubmit}
      >
        {declineError && <CardPaymentDeclineAlert declineError={declineError} />}
        <PaymentMethodSelectPanel
          initialValue={order.payment_method}
          isLoading={isLoadingPaymentRequest}
          value={selectedPaymentOption}
          options={methods}
          walletPayment={supportedWallets}
          onAdd={() => openPaymentCreateDrawer({ onSuccess: setSelectedPaymentOption })}
          onChange={setSelectedPaymentOption}
        />
      </DrawerLayout.Form>
    </DrawerLayout>
  );
};

const OrderPaymentUpdateDrawer = ({ id }: OrderPaymentUpdateDrawerProps) => {
  const { data: order, isLoading, error } = useOrderQuery(id, { enabled: !!id });

  if (isLoading) {
    return <DrawerLayout.Loading />;
  }

  if (error || !order) {
    return <DrawerLayout.FetchError entityName="order" error={error} />;
  }

  return <OrderPaymentUpdateForm order={order} />;
};

export default OrderPaymentUpdateDrawer;
